import _ from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import scrollparent from 'scrollparent';
import styled from 'styled-components';

import ChevronRightStrokeIcon from 'components/common/icons/stroke/chevron-right-stroke';
import { MenuItem } from 'components/lib/menu_deprecated';
import Portal from 'components/common/utilities/portal';
import ScrollNotifier from 'components/lib/scroll_notifier';

const VIEWPORT_MARGIN = 8;

// Stopped here so they don't also reach window-level listeners.
const SWALLOWED_KEYS = new Set([
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'End',
  'Enter',
  'Escape',
  'Home',
  ' ',
  'Tab',
]);

export default function AgentFormConfigurationMenuItem({
  actions,
  className,
  icon,
  integrationName,
  isOpen,
  onCloseFlyout,
  onEntryFocus,
  onEntryMouseEnter,
  onFlyoutMouseEnter,
  onMouseLeave,
  onOpenFlyout,
  onSelectAction,
  shouldFocusFlyout,
}) {
  const entryRef = useRef();
  const flyoutId = useMemo(() => _.uniqueId('agent-form-configuration-submenu-'), []);
  const { checkTruncation, labelRef, title } = useTitleWhenTruncated(integrationName);

  return (
    <>
      <MenuItem
        aria-controls={isOpen ? flyoutId : undefined}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className={classnames(
          'communicationControlsMenu-item',
          'communicationControlsMenu-item-agentFormConfiguration',
          className
        )}
        data-aid="agent-form-configuration-menu-item"
        onFocus={onEntryFocus}
        onKeyDown={handleEntryKeyDown}
        onMouseEnter={handleEntryMouseEnter}
        onMouseLeave={onMouseLeave}
        onSelect={handleEntrySelect}
        ref={entryRef}
        role="menuitem"
        title={title}
      >
        {icon}
        <EntryLabel ref={labelRef}>{integrationName}</EntryLabel>
        <StyledChevronIcon />
      </MenuItem>
      {isOpen && (
        <AgentFormConfigurationFlyout
          actions={actions}
          anchorRef={entryRef}
          id={flyoutId}
          integrationName={integrationName}
          onClose={handleFlyoutClose}
          onMouseEnter={onFlyoutMouseEnter}
          onMouseLeave={onMouseLeave}
          onSelectAction={onSelectAction}
          shouldFocusFirstItem={shouldFocusFlyout}
        />
      )}
    </>
  );

  function handleEntryMouseEnter(e) {
    checkTruncation();
    onEntryMouseEnter?.(e);
  }

  function handleEntryKeyDown(e) {
    if (e.key !== 'ArrowRight') {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    onOpenFlyout({ focusFlyout: true });
  }

  // Click, Enter and Space all arrive here; stopping them keeps the plus menu's document listeners from closing it.
  function handleEntrySelect(e) {
    e.stopPropagation();
    onOpenFlyout({ focusFlyout: e.type !== 'click' });
  }

  function handleFlyoutClose() {
    onCloseFlyout();
    entryRef.current?.focus();
  }
}

AgentFormConfigurationMenuItem.propTypes = {
  actions: PropTypes.array.isRequired,
  className: PropTypes.string,
  icon: PropTypes.node,
  integrationName: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  onCloseFlyout: PropTypes.func.isRequired,
  onEntryFocus: PropTypes.func,
  onEntryMouseEnter: PropTypes.func,
  onFlyoutMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onOpenFlyout: PropTypes.func.isRequired,
  onSelectAction: PropTypes.func.isRequired,
  shouldFocusFlyout: PropTypes.bool,
};

export function AgentFormConfigurationFlyout({
  actions,
  anchorRef,
  id,
  integrationName,
  onClose,
  onMouseEnter,
  onMouseLeave,
  onSelectAction,
  shouldFocusFirstItem,
}) {
  const flyoutRef = useRef();
  const [scrollParent] = useState(() => scrollparent(anchorRef.current));

  useLayoutEffect(positionFlyout);

  useLayoutEffect(() => {
    if (shouldFocusFirstItem) {
      getItems()[0]?.focus();
    }
  }, [shouldFocusFirstItem]);

  return (
    <ScrollNotifier onScroll={positionFlyout} scrollingParent={scrollParent}>
      <Portal>
        <Flyout
          aria-label={integrationName}
          className="menu menu-open"
          data-aid="agent-form-configuration-submenu"
          id={id}
          onClick={stopPropagation}
          onKeyDown={handleKeyDown}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          ref={flyoutRef}
          role="menu"
        >
          {actions.map((action, index) => (
            <FlyoutItem
              action={action}
              // An org can register the same form more than once under different labels, so formUrl alone isn't unique.
              key={`${index}-${action.formUrl}`}
              onSelectAction={onSelectAction}
            />
          ))}
        </Flyout>
      </Portal>
    </ScrollNotifier>
  );

  function getItems() {
    return flyoutRef.current ? Array.from(flyoutRef.current.querySelectorAll('[role="menuitem"]')) : [];
  }

  function handleKeyDown(e) {
    if (!SWALLOWED_KEYS.has(e.key)) {
      return;
    }
    e.stopPropagation();

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusItemAt(currentIndex() + 1);
        return;
      case 'ArrowUp':
        e.preventDefault();
        focusItemAt(currentIndex() - 1);
        return;
      case 'Home':
        e.preventDefault();
        focusItemAt(0);
        return;
      case 'End':
        e.preventDefault();
        focusItemAt(-1);
        return;
      case 'ArrowLeft':
      case 'Escape':
        e.preventDefault();
        onClose();
        return;
      case 'Tab':
        e.preventDefault();
        return;
      default:
        return;
    }
  }

  function currentIndex() {
    return getItems().indexOf(document.activeElement);
  }

  function focusItemAt(index) {
    const items = getItems();
    if (!items.length) {
      return;
    }
    items[(index + items.length) % items.length].focus();
  }

  function positionFlyout() {
    const anchor = anchorRef.current;
    const flyout = flyoutRef.current;
    if (!anchor || !flyout) {
      return;
    }

    const anchorRect = anchor.getBoundingClientRect();
    const viewport = getVisualViewport();
    const { left, top } = getFlyoutPosition(anchorRect, flyout.getBoundingClientRect(), viewport);
    // Rects are relative to the visual viewport, but the fixed flyout is placed against the layout viewport.
    flyout.style.left = `${left + viewport.offsetLeft}px`;
    flyout.style.top = `${top + viewport.offsetTop}px`;
    flyout.style.visibility = isScrolledOutOfView(anchorRect, scrollParent) ? 'hidden' : '';
  }
}

AgentFormConfigurationFlyout.propTypes = {
  actions: PropTypes.array.isRequired,
  anchorRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  id: PropTypes.string.isRequired,
  integrationName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onSelectAction: PropTypes.func.isRequired,
  shouldFocusFirstItem: PropTypes.bool,
};

function FlyoutItem({ action, onSelectAction }) {
  const { checkTruncation, labelRef, title } = useTitleWhenTruncated(action.name);

  return (
    <MenuItem
      className="communicationControlsMenu-item"
      data-aid="agent-form-configuration-submenu-item"
      onMouseEnter={handleMouseEnter}
      onSelect={() => onSelectAction(action)}
      role="menuitem"
      title={title}
    >
      <FlyoutItemLabel ref={labelRef}>{action.name}</FlyoutItemLabel>
    </MenuItem>
  );

  function handleMouseEnter(e) {
    checkTruncation();
    e.currentTarget.focus({ preventScroll: true });
  }
}

FlyoutItem.propTypes = {
  action: PropTypes.object.isRequired,
  onSelectAction: PropTypes.func.isRequired,
};

function useTitleWhenTruncated(text) {
  const labelRef = useRef();
  const [isTruncated, setIsTruncated] = useState(false);

  return { checkTruncation, labelRef, title: isTruncated ? text : undefined };

  function checkTruncation() {
    const label = labelRef.current;
    setIsTruncated(!!label && label.scrollWidth > label.clientWidth);
  }
}

// The plus menu sits low and opens upward, so the flyout often has to bottom-align with its entry.
export function getFlyoutPosition(anchorRect, flyoutRect, viewport) {
  let left = anchorRect.right;
  if (left + flyoutRect.width > viewport.width - VIEWPORT_MARGIN) {
    left = anchorRect.left - flyoutRect.width;
  }

  let top = anchorRect.top;
  if (top + flyoutRect.height > viewport.height - VIEWPORT_MARGIN) {
    top = anchorRect.bottom - flyoutRect.height;
  }

  return {
    left: Math.max(VIEWPORT_MARGIN, Math.floor(left)),
    top: Math.max(VIEWPORT_MARGIN, Math.floor(top)),
  };
}

export function isScrolledOutOfView(anchorRect, scrollParent) {
  if (!scrollParent) {
    return false;
  }
  const { bottom, top } = scrollParent.getBoundingClientRect();
  return anchorRect.bottom <= top || anchorRect.top >= bottom;
}

function getVisualViewport() {
  const vv = window.visualViewport;
  return {
    height: vv ? vv.height : window.innerHeight,
    offsetLeft: vv ? vv.offsetLeft : 0,
    offsetTop: vv ? vv.offsetTop : 0,
    width: vv ? vv.width : window.innerWidth,
  };
}

function stopPropagation(e) {
  e.stopPropagation();
}

const EntryLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledChevronIcon = styled(ChevronRightStrokeIcon)`
  && {
    color: currentColor;
    fill: currentColor;
    flex-shrink: 0;
    margin-left: ${p => p.theme.spacing.medium};
    margin-right: 0;
  }
`;

const Flyout = styled.div`
  && {
    max-height: calc(70vh - 40px);
    max-width: 320px;
    overflow-y: auto;
    position: fixed;
    z-index: 20;
  }
`;

const FlyoutItemLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;
